// components/common/ProductCategoryPicker.jsx
//
// Picker for a node in the product category tree. MUI Autocomplete with
// server-side typeahead — the taxonomy is too large for client-side
// filtering.
//
// Two modes via `scopeToStore`:
//   - false (default) — assignment. Full Shopify taxonomy. Merchant
//                       tags an Item; any category is valid.
//   - true            — targeting. Only categories assigned to the
//                       current store's items, never their parents.
//                       Merchant scopes a query/promotion.
//
// Both modes share the same MUI shell + wire shape — the choice is just
// which data hook backs it. See useProductCategorySearch for the data
// contract.
//
// `allLabel` makes the picker a filter that reads like the filter row's
// Supplier and Location dropdowns: the label stays on the border and an empty
// picker shows the "All …" text in the value's colour, not as a muted hint.
//
// Wire shape per option: { id, name, display_label }
//   - id            — TaxonomyCategory GID; the picker's value and row key
//   - display_label — "<parent> > <self>", the only text shown in the input
//                     and the row. The server also searches the full path, so
//                     a typed word can match a row without appearing in it.

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  CircularProgress,
  TextField,
} from '@mui/material';
import { useProductCategorySearch } from '../../hooks/useProductCategorySearch';

const DEBOUNCE_MS = 250;

export default function ProductCategoryPicker({
  value,
  onChange,
  label,
  size = 'small',
  required = false,
  disabled = false,
  error = false,
  helperText,
  className,
  sx,
  scopeToStore = false,
  allLabel,
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Debounce inputValue → debouncedTerm so we fire one request per
  // ~quarter-second of typing, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { categories, loading } = useProductCategorySearch(
    debouncedTerm, { scopeToStore },
  );

  // The picked value is missing from the results whenever the typed search
  // does not match it; splice it in so MUI doesn't warn about
  // value-not-in-options.
  const options = useMemo(() => {
    if (!value) return categories;
    const present = categories.some((c) => c.id === value.id);
    return present ? categories : [value, ...categories];
  }, [categories, value]);

  return (
    <Autocomplete
      value={value || null}
      onChange={(_evt, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_evt, newInput) => setInputValue(newInput)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}   // server-side search — no client filter
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      getOptionLabel={(opt) => opt?.display_label || opt?.name || ''}
      getOptionKey={(opt) => opt.id}
      noOptionsText={
        debouncedTerm.length > 0 && debouncedTerm.length < 2
          ? t('productCategory.typeMoreChars')
          : t('productCategory.noMatches')
      }
      size={size}
      disabled={disabled}
      className={className}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('productCategory.fieldLabel')}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={allLabel}
          sx={allLabel && {
            '& .MuiInputBase-input::placeholder': { color: 'var(--color-text-primary) !important' },
          }}
          slotProps={{
            ...params.slotProps,
            inputLabel: allLabel ? { ...params.slotProps.inputLabel, shrink: true } : params.slotProps.inputLabel,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
